// @ts-nocheck
/**
 * ステータス計算モジュール
 * モンスターの攻撃力・HP・防御・速度を計算
 * @module twinKey/core/party/StatCalculator
 */

import { RARITY_CONFIG, AWAKENING_BONUS, SKILL_UNLOCK_LEVELS } from '../../data/constants';
import { getAttributeModifier } from './data/attributeModifiers';
import { getTotalCategoryModifier } from './data/categoryModifiers';

/**
 * レベル倍率を計算
 * Lv.1 = ×1.0, MaxLv = maxLevelMultiplier
 * @param {number} level - 現在レベル
 * @param {number} maxLevel - 最大レベル
 * @param {number} maxLevelMultiplier - 最大レベル時の倍率
 * @returns {number}
 */
export function calculateLevelMultiplier(level, maxLevel, maxLevelMultiplier) {
  if (level <= 1) return 1.0;
  if (level >= maxLevel) return maxLevelMultiplier;

  // 線形補間
  return 1.0 + (maxLevelMultiplier - 1.0) * ((level - 1) / (maxLevel - 1));
}

/**
 * 成長ステータス（攻撃力・HP）を計算
 * 計算式: カード基礎値 × レベル倍率 × レアリティ初期倍率 × (1 + 覚醒ボーナス)
 * @param {number} baseValue - カードの基礎値
 * @param {number} level - 現在レベル
 * @param {string} rarity - レアリティ
 * @param {number} awakening - 覚醒段階（0-5）
 * @returns {number}
 */
export function calculateGrowthStat(baseValue, level, rarity, awakening = 0) {
  const config = RARITY_CONFIG[rarity] || RARITY_CONFIG.C;
  const awakeningBonus = AWAKENING_BONUS[awakening] || 0;

  const levelMultiplier = calculateLevelMultiplier(
    level,
    config.maxLevel,
    config.maxLevelMultiplier
  );

  return Math.floor(
    baseValue * levelMultiplier * config.initialMultiplier * (1 + awakeningBonus)
  );
}

/**
 * 防御を計算（固定ステータス）
 * 計算式: 属性補正 + カテゴリ補正（最低0）
 * @param {string} attribute - 属性
 * @param {string} category - カテゴリ文字列
 * @returns {number}
 */
export function calculateDefense(attribute, category) {
  const attrMod = getAttributeModifier(attribute);
  const catMod = getTotalCategoryModifier(category);

  return Math.max(0, attrMod.defense + catMod.defense);
}

/**
 * 速度を計算（固定ステータス）
 * 計算式: 100 - (攻撃力÷100) - (HP÷200) + 属性補正 + カテゴリ補正
 * @param {number} attack - 計算済み攻撃力
 * @param {number} hp - 計算済みHP
 * @param {string} attribute - 属性
 * @param {string} category - カテゴリ文字列
 * @returns {number}
 */
export function calculateSpeed(attack, hp, attribute, category) {
  const attrMod = getAttributeModifier(attribute);
  const catMod = getTotalCategoryModifier(category);

  const baseSpeed = 100 - (attack / 100) - (hp / 200);
  const totalMod = attrMod.speed + catMod.speed;

  return Math.floor(baseSpeed + totalMod);
}

/**
 * 軽減率を計算
 * 計算式: max(15%, 防御×1.5 / (100 + 防御×1.5))
 * @param {number} defense - 防御値
 * @returns {number} 0-1の範囲
 */
export function calculateDamageReduction(defense) {
  const reduction = (defense * 1.5) / (100 + defense * 1.5);
  return Math.max(0.15, reduction);
}

/**
 * スキル解放状態を取得
 * @param {number} level - 現在レベル
 * @returns {{ skill_1: boolean, skill_2: boolean, skill_3: boolean }}
 */
export function getSkillUnlockStatus(level) {
  return {
    skill_1: level >= SKILL_UNLOCK_LEVELS.skill_1,
    skill_2: level >= SKILL_UNLOCK_LEVELS.skill_2,
    skill_3: level >= SKILL_UNLOCK_LEVELS.skill_3,
  };
}

/**
 * モンスターの全ステータスを計算
 * @param {Object} card - カード情報
 * @param {number} level - 現在レベル
 * @param {string} rarity - レアリティ
 * @param {number} awakening - 覚醒段階（0-5）
 * @returns {Object} 計算済みステータス
 */
export function calculateAllStats(card, level, rarity, awakening = 0) {
  // 成長ステータス
  const attack = calculateGrowthStat(card.attack, level, rarity, awakening);
  const maxHp = calculateGrowthStat(card.hp, level, rarity, awakening);

  // 固定ステータス（カード基礎値から算出、レベルアップで変化しない）
  const defense = calculateDefense(card.attribute, card.category);
  const speed = calculateSpeed(card.attack, card.hp, card.attribute, card.category);

  // スキル解放
  const skillUnlock = getSkillUnlockStatus(level);

  return {
    attack,
    maxHp,
    defense,
    speed,
    damageReduction: calculateDamageReduction(defense),
    skillUnlock,
  };
}

/**
 * ステータス計算の詳細を取得（デバッグ用）
 * @param {Object} card - カード情報
 * @param {number} level - 現在レベル
 * @param {string} rarity - レアリティ
 * @param {number} awakening - 覚醒段階
 * @returns {Object} 詳細情報
 */
export function getStatCalculationDetails(card, level, rarity, awakening = 0) {
  const config = RARITY_CONFIG[rarity] || RARITY_CONFIG.C;
  const awakeningBonus = AWAKENING_BONUS[awakening] || 0;
  const levelMultiplier = calculateLevelMultiplier(
    level,
    config.maxLevel,
    config.maxLevelMultiplier
  );

  const attrMod = getAttributeModifier(card.attribute);
  const catMod = getTotalCategoryModifier(card.category);

  const stats = calculateAllStats(card, level, rarity, awakening);

  return {
    // 入力
    input: {
      cardName: card.name,
      baseAttack: card.attack,
      baseHp: card.hp,
      attribute: card.attribute,
      category: card.category,
      level,
      rarity,
      awakening,
    },
    // 計算パラメータ
    params: {
      levelMultiplier: levelMultiplier.toFixed(3),
      initialMultiplier: config.initialMultiplier,
      maxLevel: config.maxLevel,
      awakeningBonus: (awakeningBonus * 100).toFixed(0) + '%',
      attributeModifier: attrMod,
      categoryModifier: catMod,
    },
    // 結果
    result: stats,
  };
}
